import React from "react";
import { Film } from "../../../types";

interface FilmModalProps {
  film: Film;
  isOpen: boolean;
  onClose: () => void;
}

const FilmModal: React.FC<FilmModalProps> = ({ film, isOpen, onClose }) => {
  if (!isOpen) return null;

  // Get thumbnail (always return a usable URL)
  const getThumbnail = () => {
    if (!film) return "https://placehold.co/400x600/1f2937/ffffff/png?text=Image+Not+Found";

    let url: string | undefined;
    if (film.thumbnails) {
      try {
        const thumbs = typeof film.thumbnails === "string" ? JSON.parse(film.thumbnails) : film.thumbnails;
        url = thumbs[1]?.url || thumbs[0]?.url || film.primary_image;
      } catch {
        url = film.primary_image;
      }
    } else {
      url = film.primary_image;
    }

    return url || "https://placehold.co/400x600/1f2937/ffffff/png?text=Image+Not+Found";
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="bg-gray-900 border-2 border-red-600 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-[0_0_40px_rgba(220,38,38,0.5)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors">
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="flex flex-col md:flex-row gap-6 p-6">
          {/* Poster */}
          <div className="flex-shrink-0">
            <img
              src={getThumbnail()}
              alt={film.original_title}
              className="w-full md:w-64 h-auto object-cover rounded-lg border-2 border-red-600 shadow-lg"
              onError={(e) => {
                const target = e.currentTarget as HTMLImageElement;
                target.onerror = null;
                target.src = "https://placehold.co/400x600/1f2937/ffffff/png?text=Image+Not+Found";
              }}
            />
          </div>

          {/* Info */}
          <div className="flex-1 space-y-4">
            <h2 className="text-3xl font-bold text-white">{film.original_title}</h2>

            <div className="flex flex-wrap gap-4 text-sm">
              <span className="px-3 py-1 bg-red-600 text-white rounded-full font-semibold">
                {film.type === "tvSeries" ? "TV Series" : "Movie"}
              </span>
              <span className="px-3 py-1 bg-gray-800 text-gray-300 rounded-full">{film.start_year}</span>
              {film.average_rating && (
                <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full font-semibold">
                  ⭐ {Number(film.average_rating).toFixed(1)}
                </span>
              )}
              {film.runtime_minutes && <span className="px-3 py-1 bg-gray-800 text-gray-300 rounded-full">{film.runtime_minutes} min</span>}
            </div>

            {/* Genres */}
            {film.genres && (
              <div className="flex flex-wrap gap-2">
                {(typeof film.genres === "string" ? JSON.parse(film.genres) : film.genres).map((genre: string, i: number) => (
                  <span key={i} className="px-2 py-1 bg-gray-800 text-cyan-400 text-xs rounded border border-cyan-700">
                    {genre}
                  </span>
                ))}
              </div>
            )}

            {/* Description */}
            {film.description && (
              <div>
                <h3 className="text-lg font-semibold text-red-500 mb-2">Overview</h3>
                <p className="text-gray-300 leading-relaxed">{film.description}</p>
              </div>
            )}

            {/* Additional Info */}
            <div className="pt-4 border-t border-gray-800 space-y-2 text-sm">
              {film.release_date && (
                <div className="flex gap-2">
                  <span className="text-gray-500">Release Date:</span>
                  <span className="text-gray-300">{new Date(film.release_date).toLocaleDateString()}</span>
                </div>
              )}
              {film.imdb_id && (
                <div className="flex gap-2">
                  <span className="text-gray-500">IMDB ID:</span>
                  <a
                    href={`https://www.imdb.com/title/${film.imdb_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-cyan-400 hover:text-cyan-300 underline"
                  >
                    {film.imdb_id}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilmModal;
